import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { useAuth } from "@/auth/AuthProvider";
import { api } from "@/lib/api";

type Industry = { id: number; code: string; name: string };

type Shop = {
  id: number;
  code: string;
  name: string;
  poscake_shop_id: number | null;
  default_industry_id: number | null;
  active: boolean;
  webhook_enabled: boolean;
  has_poscake_api_key: boolean;
  poscake_api_key_masked: string | null;
  last_pos_cursor: string | null;
};

type TestResult = { ok: boolean; [k: string]: any };

export function ShopsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const qc = useQueryClient();

  const shopsQ = useQuery({
    queryKey: ["shops"],
    queryFn: async () => (await api.get<Shop[]>("/shops")).data,
  });
  const industriesQ = useQuery({
    queryKey: ["industries"],
    queryFn: async () => (await api.get<Industry[]>("/industries")).data,
  });

  const [editing, setEditing] = useState<Shop | null>(null);
  const [creating, setCreating] = useState(false);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);

  const syncAllMut = useMutation({
    mutationFn: async () => (await api.post("/shops/sync-all")).data,
    onSuccess: () => {
      setSyncStatus("Đã enqueue đồng bộ POScake + VTP cho tất cả shop. Mất ~1-5 phút.");
      setTimeout(() => setSyncStatus(null), 8000);
    },
    onError: (e: any) =>
      setSyncStatus("Lỗi: " + (e?.response?.data?.detail || String(e))),
  });

  const syncShopMut = useMutation({
    mutationFn: async (shopId: number) =>
      (await api.post(`/shops/${shopId}/sync`)).data,
    onSuccess: (_, shopId) => {
      setSyncStatus(`Đã enqueue sync cho shop #${shopId}.`);
      setTimeout(() => setSyncStatus(null), 5000);
    },
    onError: (e: any) =>
      setSyncStatus("Lỗi: " + (e?.response?.data?.detail || String(e))),
  });

  const backfillMut = useMutation({
    mutationFn: async ({ shopId, days }: { shopId: number; days: number }) =>
      (await api.post(`/shops/${shopId}/backfill?days=${days}`)).data,
    onSuccess: (_, { shopId, days }) => {
      setSyncStatus(
        `Đã enqueue backfill ${days} ngày cho shop #${shopId}. Job chạy background ~2-10 phút.`
      );
      setTimeout(() => setSyncStatus(null), 8000);
    },
    onError: (e: any) =>
      setSyncStatus("Lỗi: " + (e?.response?.data?.detail || String(e))),
  });

  const deleteShopMut = useMutation({
    mutationFn: async (shopId: number) =>
      (await api.delete<{ shop_name: string; cascaded: Record<string, number> }>(
        `/shops/${shopId}`,
      )).data,
    onSuccess: (r) => {
      const parts = Object.entries(r.cascaded)
        .filter(([, n]) => n > 0)
        .map(([k, n]) => `${n.toLocaleString("vi-VN")} ${k}`)
        .join(", ");
      setSyncStatus(
        `✓ Đã xoá shop "${r.shop_name}"` +
          (parts ? ` + ${parts}` : " (không có data liên quan)"),
      );
      setTimeout(() => setSyncStatus(null), 10000);
      qc.invalidateQueries({ queryKey: ["shops"] });
    },
    onError: (e: any) =>
      setSyncStatus("Lỗi xoá: " + (e?.response?.data?.detail || String(e))),
  });

  function confirmDelete(s: Shop) {
    const typed = prompt(
      `Xoá shop "${s.name}" sẽ XOÁ VĨNH VIỄN toàn bộ đơn hàng, đối soát, ` +
        `nhân viên, mapping mã hàng liên quan. Không thể phục hồi!\n\n` +
        `Nhập tên shop "${s.name}" để xác nhận:`,
    );
    if (typed?.trim() === s.name) {
      deleteShopMut.mutate(s.id);
    } else if (typed !== null) {
      alert("Tên nhập KHÔNG khớp — huỷ xoá.");
    }
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-semibold text-slate-900">Cửa hàng POScake</h1>
        {isAdmin && (
          <div className="flex gap-2">
            <button
              onClick={() => syncAllMut.mutate()}
              disabled={syncAllMut.isPending}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-60"
            >
              {syncAllMut.isPending ? "Đang xếp hàng..." : "↻ Đồng bộ tất cả"}
            </button>
            <button
              onClick={() => setCreating(true)}
              className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
            >
              + Thêm cửa hàng
            </button>
          </div>
        )}
      </div>

      {syncStatus && (
        <div className="mb-3 bg-blue-50 border border-blue-200 text-blue-800 rounded px-3 py-2 text-sm">
          {syncStatus}
        </div>
      )}

      {shopsQ.isLoading && <div className="text-gray-500">Đang tải...</div>}
      {shopsQ.data && (
        <table className="w-full bg-white border border-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-3 py-2">Mã</th>
              <th className="text-left px-3 py-2">Tên</th>
              <th className="text-left px-3 py-2">POScake ID</th>
              <th className="text-left px-3 py-2">API key</th>
              <th className="text-left px-3 py-2">Webhook</th>
              <th className="text-left px-3 py-2">Hoạt động</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {shopsQ.data.map((s) => (
              <tr key={s.id} className="border-t border-gray-100">
                <td className="px-3 py-2 font-mono text-xs">{s.code}</td>
                <td className="px-3 py-2">{s.name}</td>
                <td className="px-3 py-2">
                  {s.poscake_shop_id ?? (
                    s.has_poscake_api_key ? (
                      <span className="text-yellow-700 text-xs" title="Có API key nhưng chưa link shop POScake — bấm Sửa để fix">
                        ⚠️ thiếu
                      </span>
                    ) : "—"
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  {s.has_poscake_api_key ? s.poscake_api_key_masked : "—"}
                </td>
                <td className="px-3 py-2">{s.webhook_enabled ? "Bật" : "Tắt"}</td>
                <td className="px-3 py-2">{s.active ? "✓" : "—"}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {isAdmin && (
                    <>
                      <button
                        onClick={() => syncShopMut.mutate(s.id)}
                        disabled={syncShopMut.isPending}
                        className="text-gray-600 hover:text-gray-900 text-xs mr-2"
                        title="Sync incremental (từ cursor cũ)"
                      >
                        ↻ Sync
                      </button>
                      <button
                        onClick={() => {
                          const daysStr = prompt(
                            "Backfill bao nhiêu ngày lịch sử? (1-365)",
                            "120"
                          );
                          if (!daysStr) return;
                          const days = parseInt(daysStr);
                          if (isNaN(days) || days < 1 || days > 365) return;
                          backfillMut.mutate({ shopId: s.id, days });
                        }}
                        disabled={backfillMut.isPending}
                        className="text-amber-700 hover:text-amber-900 text-xs mr-2 font-medium"
                        title="Pull toàn bộ lịch sử đơn (bỏ qua cursor, chạy background)"
                      >
                        ⏬ Backfill
                      </button>
                      <button
                        onClick={() => setEditing(s)}
                        className="text-blue-700 hover:text-blue-900 text-sm mr-2"
                      >
                        Sửa
                      </button>
                      <button
                        onClick={() => confirmDelete(s)}
                        disabled={deleteShopMut.isPending}
                        className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
                        title="Xoá shop + toàn bộ đơn / đối soát / mapping liên quan"
                      >
                        Xoá
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {shopsQ.data.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-gray-500 py-6">
                  Chưa có cửa hàng. Bấm "+ Thêm cửa hàng" để bắt đầu.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {editing && (
        <ShopDrawer
          shop={editing}
          industries={industriesQ.data || []}
          onClose={() => setEditing(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ["shops"] })}
        />
      )}

      {creating && (
        <CreateShopDrawer
          industries={industriesQ.data || []}
          onClose={() => setCreating(false)}
          onCreated={(shop) => {
            qc.invalidateQueries({ queryKey: ["shops"] });
            setCreating(false);
            setEditing(shop); // open the full editor so user can finish setup
          }}
        />
      )}
    </div>
  );
}

function CreateShopDrawer({
  industries,
  onClose,
  onCreated,
}: {
  industries: Industry[];
  onClose: () => void;
  onCreated: (s: Shop) => void;
}) {
  const [form, setForm] = useState({
    code: "",
    name: "",
    poscake_shop_id: "",
    poscake_api_key: "",
    default_industry_id: "" as number | "",
    active: true,
  });
  const [probed, setProbed] = useState<{ id: number; name: string }[] | null>(null);
  const [probeError, setProbeError] = useState<string | null>(null);

  const probe = useMutation({
    mutationFn: async () => {
      const r = await api.post<{ ok: boolean; shops: { id: number; name: string }[]; error?: string }>(
        "/shops/probe-poscake",
        { api_key: form.poscake_api_key.trim() }
      );
      return r.data;
    },
    onSuccess: (data) => {
      if (!data.ok) {
        setProbeError(data.error || "Không lấy được danh sách shop");
        setProbed(null);
        return;
      }
      setProbeError(null);
      setProbed(data.shops);
      // Nếu chỉ 1 shop → auto chọn luôn
      if (data.shops.length === 1) {
        const only = data.shops[0];
        setForm((f) => ({
          ...f,
          poscake_shop_id: String(only.id),
          name: f.name || only.name,
        }));
      }
    },
    onError: (e: any) => setProbeError(e?.response?.data?.detail ?? String(e)),
  });

  // Khi chọn shop_id từ dropdown → auto điền name nếu chưa có
  const selectShop = (id: string) => {
    setForm((f) => {
      const found = probed?.find((s) => String(s.id) === id);
      return { ...f, poscake_shop_id: id, name: found?.name && !f.name ? found.name : f.name };
    });
  };

  const create = useMutation({
    mutationFn: async () => {
      const body: any = {
        code: form.code.trim(),
        name: form.name.trim() || form.code.trim(),
        active: form.active,
      };
      if (form.poscake_shop_id) body.poscake_shop_id = Number(form.poscake_shop_id);
      if (form.poscake_api_key) body.poscake_api_key = form.poscake_api_key.trim();
      if (form.default_industry_id !== "")
        body.default_industry_id = form.default_industry_id;
      return (await api.post<Shop>("/shops", body)).data;
    },
    onSuccess: (data) => onCreated(data),
  });

  return (
    <div className="fixed inset-0 bg-black/30 flex justify-end z-50">
      <div className="w-full max-w-lg bg-white h-full overflow-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Thêm cửa hàng mới</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900">
            ✕
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
          className="space-y-3"
        >
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Mã shop *</label>
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                required
                placeholder="dem-nl"
                className="w-full border rounded px-2 py-1.5 text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Tên</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="(tự lấy từ POScake)"
                className="w-full border rounded px-2 py-1.5 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Ngành mặc định</label>
            <select
              value={form.default_industry_id}
              onChange={(e) =>
                setForm({
                  ...form,
                  default_industry_id: e.target.value ? Number(e.target.value) : "",
                })
              }
              className="w-full border rounded px-2 py-1.5 text-sm"
            >
              <option value="">— Không —</option>
              {industries.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.code} — {i.name}
                </option>
              ))}
            </select>
          </div>

          <div className="border-t pt-3 mt-3">
            <div className="text-sm font-medium text-gray-700 mb-2">
              POScake
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">API key *</label>
              <div className="flex gap-2">
                <input
                  type="password"
                  value={form.poscake_api_key}
                  onChange={(e) => {
                    setForm({ ...form, poscake_api_key: e.target.value });
                    setProbed(null);
                    setProbeError(null);
                  }}
                  placeholder="5490a84d..."
                  autoComplete="new-password"
                  className="flex-1 border rounded px-2 py-1.5 text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={() => probe.mutate()}
                  disabled={probe.isPending || !form.poscake_api_key.trim()}
                  className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  {probe.isPending ? "Đang tải..." : "Lấy DS shop"}
                </button>
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                Gõ api_key xong bấm "Lấy DS shop" — tên + ID shop tự fill từ POScake.
              </div>
            </div>

            {probeError && (
              <div className="mt-2 bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">
                {probeError}
              </div>
            )}

            {probed && probed.length > 0 && (
              <div className="mt-2">
                <label className="block text-xs text-gray-600 mb-0.5">
                  Chọn shop POScake * ({probed.length} shop)
                </label>
                <select
                  value={form.poscake_shop_id}
                  onChange={(e) => selectShop(e.target.value)}
                  className="w-full border rounded px-2 py-1.5 text-sm"
                  required
                >
                  <option value="">— Chọn shop —</option>
                  {probed.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} (ID: {s.id})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {!probed && (
              <div className="mt-2">
                <label className="block text-xs text-gray-600 mb-0.5">
                  POScake Shop ID (nhập tay nếu không probe)
                </label>
                <input
                  type="number"
                  value={form.poscake_shop_id}
                  onChange={(e) => setForm({ ...form, poscake_shop_id: e.target.value })}
                  placeholder="1234567890"
                  className="w-full border rounded px-2 py-1.5 text-sm font-mono"
                />
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm pt-2">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Đang hoạt động
          </label>

          {create.error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">
              {(create.error as any)?.response?.data?.detail ?? String(create.error)}
            </div>
          )}

          <div className="text-xs text-gray-500">
            Sau khi tạo, drawer sửa sẽ mở để anh bấm "Bật webhook".
            VTP credentials cấu hình tách riêng tại trang <b>Tài khoản VTP</b>.
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
            >
              Huỷ
            </button>
            <button
              type="submit"
              disabled={create.isPending}
              className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-60"
            >
              {create.isPending ? "Đang tạo..." : "Tạo shop"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ShopDrawer({
  shop,
  industries,
  onClose,
  onSaved,
}: {
  shop: Shop;
  industries: Industry[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(shop.name);
  const [defaultIndustryId, setDefaultIndustryId] = useState<number | "">(
    shop.default_industry_id ?? "",
  );
  const [active, setActive] = useState(shop.active);

  const [poscakeShopId, setPoscakeShopId] = useState<string>(
    shop.poscake_shop_id != null ? String(shop.poscake_shop_id) : "",
  );
  const [poscakeApiKey, setPoscakeApiKey] = useState("");
  const [probedShops, setProbedShops] = useState<{ id: number; name: string }[] | null>(null);

  const [testResult, setTestResult] = useState<{ kind: string; result: TestResult } | null>(null);

  // Reset when switching shop
  useEffect(() => {
    setName(shop.name);
    setDefaultIndustryId(shop.default_industry_id ?? "");
    setActive(shop.active);
    setPoscakeShopId(shop.poscake_shop_id != null ? String(shop.poscake_shop_id) : "");
    setPoscakeApiKey("");
    setProbedShops(null);
    setTestResult(null);
  }, [shop]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const body: Record<string, any> = {
        name,
        default_industry_id: defaultIndustryId === "" ? null : defaultIndustryId,
        active,
      };
      if (poscakeShopId !== "") body.poscake_shop_id = Number(poscakeShopId);
      if (poscakeApiKey.trim()) body.poscake_api_key = poscakeApiKey.trim();
      return (await api.patch(`/shops/${shop.id}`, body)).data as Shop;
    },
    onSuccess: () => {
      onSaved();
      setPoscakeApiKey("");
    },
  });

  const applyProbeResult = (shops: { id: number; name: string }[]) => {
    setProbedShops(shops);
    // Auto-select khi chỉ có 1 shop
    if (shops.length === 1) {
      const only = shops[0];
      setPoscakeShopId(String(only.id));
      if (!name || name.trim() === shop.code) setName(only.name);
    } else if (shops.length > 1 && poscakeShopId) {
      // nhiều shop — nếu poscakeShopId hiện tại match 1 cái → giữ, ngược lại reset
      if (!shops.find((s) => String(s.id) === poscakeShopId)) {
        setPoscakeShopId("");
      }
    }
  };

  const testPoscakeMut = useMutation({
    mutationFn: async () => (await api.post(`/shops/${shop.id}/test-poscake`)).data as TestResult,
    onSuccess: (r) => {
      setTestResult({ kind: "POScake", result: r });
      if (r.ok && Array.isArray((r as any).shops)) {
        applyProbeResult((r as any).shops);
      }
    },
    onError: (e: any) =>
      setTestResult({ kind: "POScake", result: { ok: false, error: e?.response?.data?.detail || String(e) } }),
  });

  // Probe khi user vừa nhập api key mới (chưa lưu) — dùng endpoint probe-poscake
  const probeNewKeyMut = useMutation({
    mutationFn: async () => {
      const r = await api.post<{ ok: boolean; shops: { id: number; name: string }[]; error?: string }>(
        "/shops/probe-poscake",
        { api_key: poscakeApiKey.trim() }
      );
      return r.data;
    },
    onSuccess: (data) => {
      if (data.ok) applyProbeResult(data.shops);
      setTestResult({
        kind: "Probe (key mới)",
        result: { ok: data.ok, error: data.error, shops: data.shops },
      });
    },
    onError: (e: any) =>
      setTestResult({
        kind: "Probe",
        result: { ok: false, error: e?.response?.data?.detail || String(e) },
      }),
  });

  const setupWebhookMut = useMutation({
    mutationFn: async () => (await api.post(`/shops/${shop.id}/setup-webhook`)).data as TestResult,
    onSuccess: (r) => {
      setTestResult({ kind: "Webhook", result: r });
      onSaved();
    },
    onError: (e: any) =>
      setTestResult({ kind: "Webhook", result: { ok: false, error: e?.response?.data?.detail || String(e) } }),
  });

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={onClose}>
      <div
        className="bg-white w-[560px] h-full overflow-y-auto p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Sửa cửa hàng: {shop.code}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
            ✕
          </button>
        </div>

        <div className="space-y-5 text-sm">
          {/* GENERAL */}
          <section className="space-y-2">
            <SectionTitle>Thông tin chung</SectionTitle>
            <Field label="Tên">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1.5"
              />
            </Field>
            <Field label="Ngành mặc định">
              <select
                value={defaultIndustryId}
                onChange={(e) =>
                  setDefaultIndustryId(e.target.value ? Number(e.target.value) : "")
                }
                className="w-full border border-gray-300 rounded px-2 py-1.5"
              >
                <option value="">— Không —</option>
                {industries.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.code} — {i.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Hoạt động">
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
                <span>Đang hoạt động</span>
              </label>
            </Field>
          </section>

          {/* POSCAKE */}
          <section className="space-y-2">
            <SectionTitle>POScake (Pancake POS)</SectionTitle>

            {shop.has_poscake_api_key && !shop.poscake_shop_id && (
              <div className="bg-yellow-50 border border-yellow-300 text-yellow-900 rounded px-3 py-2 text-xs">
                ⚠️ Shop này có API key nhưng <b>chưa có POScake Shop ID</b> → sync KHÔNG chạy.
                Bấm "Lấy DS shop" bên dưới rồi chọn shop để fix.
              </div>
            )}

            <Field label="POScake Shop ID *">
              <div className="flex gap-2">
                <input
                  type="number"
                  value={poscakeShopId}
                  onChange={(e) => setPoscakeShopId(e.target.value)}
                  className="flex-1 border border-gray-300 rounded px-2 py-1.5 font-mono text-xs"
                  placeholder="1234567890"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (poscakeApiKey.trim()) probeNewKeyMut.mutate();
                    else if (shop.has_poscake_api_key) testPoscakeMut.mutate();
                  }}
                  disabled={
                    (probeNewKeyMut.isPending || testPoscakeMut.isPending) ||
                    (!shop.has_poscake_api_key && !poscakeApiKey.trim())
                  }
                  className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
                  title="Gọi POScake để lấy danh sách shop"
                >
                  {(probeNewKeyMut.isPending || testPoscakeMut.isPending)
                    ? "Đang tải..."
                    : "Lấy DS shop"}
                </button>
              </div>
            </Field>

            {probedShops && (
              <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs">
                <div className="text-blue-700 mb-1">
                  {probedShops.length === 1
                    ? "✓ Đã tự chọn shop duy nhất:"
                    : `POScake trả về ${probedShops.length} shop. Click để chọn:`}
                </div>
                <div className="space-y-1">
                  {probedShops.map((s) => {
                    const selected = String(s.id) === poscakeShopId;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => {
                          setPoscakeShopId(String(s.id));
                          if (!name || name.trim() === shop.code) setName(s.name);
                        }}
                        className={`w-full text-left px-2 py-1 rounded border ${
                          selected
                            ? "bg-green-100 border-green-500 font-medium text-green-900"
                            : "bg-white border-gray-200 hover:bg-blue-50"
                        }`}
                      >
                        {selected && "✓ "}
                        {s.name} <span className="text-gray-500">(ID: {s.id})</span>
                      </button>
                    );
                  })}
                </div>
                {poscakeShopId && (
                  <div className="text-green-700 mt-2 font-medium">
                    → Sau khi bấm "Lưu" sẽ gắn shop_id = {poscakeShopId}
                  </div>
                )}
              </div>
            )}

            <Field
              label="API key"
              hint={
                shop.has_poscake_api_key
                  ? `Đã có: ${shop.poscake_api_key_masked}. Bỏ trống để giữ nguyên.`
                  : "Dán api key — sau đó bấm 'Lấy DS shop' để fetch."
              }
            >
              <input
                type="password"
                value={poscakeApiKey}
                onChange={(e) => setPoscakeApiKey(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1.5 font-mono text-xs"
                placeholder={shop.has_poscake_api_key ? "(không đổi)" : "5490a84d..."}
                autoComplete="off"
              />
            </Field>
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => setupWebhookMut.mutate()}
                disabled={setupWebhookMut.isPending || !shop.has_poscake_api_key || !shop.poscake_shop_id}
                className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                title={!shop.poscake_shop_id ? "Phải có POScake Shop ID trước" : ""}
              >
                {setupWebhookMut.isPending
                  ? "Đang cấu hình..."
                  : shop.webhook_enabled
                  ? "Cập nhật webhook"
                  : "Bật webhook"}
              </button>
            </div>
          </section>

          <div className="text-xs text-gray-500 italic">
            Tài khoản Viettel Post quản lý ở trang riêng <b>Tài khoản VTP</b> (chung
            cho mọi shop).
          </div>

          {testResult && (
            <div
              className={`border rounded p-3 text-sm ${
                testResult.result.ok
                  ? "border-green-300 bg-green-50 text-green-800"
                  : "border-red-300 bg-red-50 text-red-800"
              }`}
            >
              <div className="font-medium mb-1">
                {testResult.result.ok ? "✓" : "✗"} Test {testResult.kind}
              </div>
              <pre className="text-xs whitespace-pre-wrap">
                {JSON.stringify(testResult.result, null, 2)}
              </pre>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
            >
              Đóng
            </button>
            <button
              onClick={() => saveMut.mutate()}
              disabled={saveMut.isPending}
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50"
            >
              {saveMut.isPending ? "Đang lưu..." : "Lưu"}
            </button>
          </div>
          {saveMut.isError && (
            <div className="text-sm text-red-600">
              {(saveMut.error as any)?.response?.data?.detail || "Lỗi không xác định khi lưu"}
            </div>
          )}
          {saveMut.isSuccess && (
            <div className="text-sm text-green-700">Đã lưu thay đổi.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 pb-1 mb-2">
      {children}
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs text-gray-600 mb-1">{label}</label>
      {children}
      {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}
